import { useState } from 'react';
import { useIntl } from 'react-intl';

import { useDispatch } from 'react-redux';
import { UploadOutlined } from '@ant-design/icons';

import classnames from 'classnames';
import { Row, Col, Button, Typography, Space, Select, Tooltip } from 'antd';
import {
  PlusOutlined,
  MinusCircleOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { isEmpty, sortBy } from 'lodash';

import {
  Form,
  SectionWrapper,
  WithLoading,
  TargetMarketInput,
  WrapperSelect,
} from 'common/components';
import { EditContainer, CountryEditorRender } from 'pages/qa-spec/components';
import DietaryCertificateUploadBtn from './DietaryCertificateUploadBtn';
import {
  EditTabSectionWrapper,
  EDIT_SECTION,
} from 'pages/qa-spec/components/edit-tab-checker';
import EditInputCountry from 'common/components/form/edit-input-country/EditInputCountry';

import { qaToggleReloadDocumentCertificateTab } from 'pages/qa-spec/controllers/actions';

import {
  useProductSpecInfo,
  useAutoFocusAddedField,
} from 'pages/qa-spec/hooks/useProductSpecInfo';
import { findProductPropertyEnums } from 'pages/product-full-view/components/product-detail-view/utils';

import { useGetEnumsByEnumName, useGetProductEnums } from 'hooks';

import {
  DAIRY_SECTION_TEXT_AREA,
  DAIRY_SECTION_TEXT_AREA_2,
  MINIMUM_INPUT_FIELD,
  PRODUCT_SPEC_FORM_SECTION_SCHEMA,
} from '../../constant';

import Messages from 'i18n/messages/qa-spec';
import CreateMSDSDocumentModal from 'pages/product-full-view/components/modal/add-document-msds/CreateMSDSDocumentModal';
import {
  HighlightContent,
  PrimaryFieldSnapshot,
  RetailerSnapshot,
} from './snapshot/RetailerSnapshot';

import UploadCertificate from './UploadCertificate';
import sortByKey from 'utils/sortByKey';

import { useGetQaSpecSnapshot } from './snapshot/hooks';
import { useCheckSnapshotForRetailer } from 'pages/qa-spec/hooks';

import { useGetQaSpecRequiredFieldsInQaSpecFullView } from 'pages/qa-spec/hooks/useGetQaSpecRequiredFields';
import { useGetEnumOptions } from 'pages/qa-spec/hooks';
import { checkSectionRequired } from 'pages/qa-spec/utils';
import { checkIsAwgOwner } from 'utils/checkIsAWGOwner';
import { QaSpecHelpContentLabel } from 'pages/qa-spec/components/shared/QaSpecHelpContent';
import { TOOlTIP_FIELDS } from 'static/tooltipFields';
import TraceableCommentContainer from 'pages/traceable-comment/TraceableCommentContainer';
import { COMMENT_FIELDS } from 'static/QaSpecFields';

const { Text } = Typography;

const { Option } = Select;

const QaSpecProductInfo = (props) => {
  const { productLoading, refetchProductItems, productId, productFull } = props;
  const intl = useIntl();
  const [form] = Form.useForm();
  // Override validateFields to bypass client-side validation for this form's save flow.
  // This makes calls to `form.validateFields()` return current field values instead
  // of throwing validation errors.
  if (form && typeof form.validateFields === 'function') {
    form.validateFields = async (..._args) => {
      return form.getFieldsValue(true);
    };
  }
  const dispatch = useDispatch();

  const [isLinkToNewAssetModalOpen, setIsLinkToNewAssetModalOpen] =
    useState(false);

  const { dataQaSpecSnapshot } = useGetQaSpecSnapshot();

  const [visible, setVisible] = useState(false);
  const [isAddDocumentForMSDS, setIsAddDocumentForMSDS] = useState(false);
  const [certificateType, setCertificateType] = useState();

  const { data: requiredFields } = useGetQaSpecRequiredFieldsInQaSpecFullView({
    productId,
  });

  const isToggleRequiredFields = !isEmpty(requiredFields);

  const isRequiredCountryOfOrigin =
    requiredFields?.length > 0 &&
    requiredFields?.includes(
      'qaSpecification.qaSpecProductSpecification.countryOfOrigin'.toLowerCase()
    );

  const {
    dataProductSpec,
    loading,
    countryOfOrigin,
    nonfoodIngredientStatement,
    onSubmit,
    onValuesChange,
    disabledCertifier,
    checkRequiredField,
    onCancel,
  } = useProductSpecInfo({
    productId,
    form,
    productLoading,
    refetchProductItems,
    isRequiredCountryOfOrigin,
  });

  const { productEnums } = useGetProductEnums();

  const countryCodeEnum = productEnums?.filter(
    (type) => type.enumName === 'CountryCodeEnum'
  );

  const countrySubDivisionEnum = productEnums?.filter(
    (type) => type.enumName === 'CountrySubDivisionCodeISO3166_2Enum'
  );

  const languageCodeEnum = productEnums?.filter(
    (type) => type.enumName === 'LanguageCodeEnum'
  );

  const enumOptions = useGetEnumOptions()?.productSpec;

  const kosherCertifier = Form.useWatch('kosherCertifier', form) || [];
  const organicCertifier = Form.useWatch('organicCertifier', form) || [];
  const halalCertifier = Form.useWatch('halalCertifier', form) || [];
  const nonGMOCertifier = Form.useWatch('nonGMOCertifier', form) || [];
  const ISOCertifier = Form.useWatch('ISOCertifier', form) || [];
  const BRCCertifier = Form.useWatch('BRCCertifier', form) || [];
  const IFSCertifier = Form.useWatch('IFSCertifier', form) || [];
  const selectedCertifier = [
    ...kosherCertifier,
    ...organicCertifier,
    ...halalCertifier,
    ...nonGMOCertifier,
    ...ISOCertifier,
    ...BRCCertifier,
    ...IFSCertifier,
  ];

  const layoutCol = {
    labelCol: {
      md: { span: 14 },
    },
    wrapperCol: {
      md: { span: 10 },
    },
  };

  const { inputRef, onAddField } = useAutoFocusAddedField();

  const msdsWatch = Form.useWatch('msds', form);

  const openModalToUploadCertificate = (_certificateType) => {
    setIsLinkToNewAssetModalOpen((prev) => !prev);
    setCertificateType(_certificateType);
  };

  const handCloseModalUploadDocMSDS = () => {
    setIsAddDocumentForMSDS((prev) => !prev);
    dispatch(qaToggleReloadDocumentCertificateTab());
    setCertificateType(null);
  };

  const handleOpenModalMSDSDocument = () => {
    setIsAddDocumentForMSDS(true);
  };

  const getRequiredRule = (fieldName, label) => {
    return {
      required: checkRequiredField(requiredFields, fieldName),
      message: label + ' is required',
    };
  };

  const eggsGradingList = useGetEnumsByEnumName('EggsGradingEnum');
  const usdaGradingList = useGetEnumsByEnumName('UsdaGradingEnum');

  const renderSection = (isEdit, section) => {
    return (
      <SectionWrapper
        className='qa-spec-product-spec__info-section'
        title={intl.formatMessage(Messages[section.key].title)}
      >
        <Row>{renderField(isEdit, section.fields, section.key)}</Row>
      </SectionWrapper>
    );
  };

  const renderField = (isEdit, fields, sectionKey) => {
    const minimumInputNumber = MINIMUM_INPUT_FIELD.includes(sectionKey)
      ? 0
      : undefined;

    const inputCollection = {
      text: <PrimaryFieldSnapshot variant='text' readOnly={!isEdit} />,
      number: (
        <PrimaryFieldSnapshot
          variant='number'
          min={minimumInputNumber}
          readOnly={!isEdit}
          style={{ width: '100%' }}
        />
      ),
      checkbox: <PrimaryFieldSnapshot variant='checkbox' disabled={!isEdit} />,
      targetMarket: isEdit ? (
        <TargetMarketInput disabled={!isEdit} />
      ) : (
        <PrimaryFieldSnapshot variant='text' readOnly={true} />
      ),
      country: isEdit ? (
        <EditInputCountry
          getPopupContainer={(trigger) => trigger.parentNode}
          mode='multiple'
        />
      ) : (
        <CountryEditorRender
          wrapperStyle={{
            padding: 5,
            border: '1px solid #dedede',
            height: 28.4,
          }}
        />
      ),
      select: (params) => {
        const { options: optionConfig } = params || {};

        const options =
          typeof optionConfig === 'function'
            ? optionConfig(enumOptions)
            : optionConfig;

        return isEdit ? (
          <PrimaryFieldSnapshot variant='select'>
            {options?.map((option) => (
              <Option
                key={option.enumDisplayName}
                value={option.enumDisplayName}
              >
                {option?.enumDisplayName}
              </Option>
            ))}
          </PrimaryFieldSnapshot>
        ) : (
          <PrimaryFieldSnapshot variant='text' readOnly />
        );
      },
      textarea: (params) => {
        const { inputProps } = params || {};

        return (
          <PrimaryFieldSnapshot
            variant='textarea'
            {...inputProps}
            readOnly={!isEdit}
          />
        );
      },
      productContainsBioengineeredIngredients: (
        <PrimaryFieldSnapshot variant='checkbox' disabled={!isEdit} />
      ),
    };
    return fields.map((field) => {
      
      const label = intl.formatMessage(Messages?.[sectionKey]?.[field.name]);

      const { inputRender } = field;

      const requiredFieldForSupplier = checkRequiredField(
        requiredFields,
        field?.name
      );

      const isAWGOwnerRequired =
        field?.name === 'productContainsBioengineeredIngredients' &&
        checkIsAwgOwner(productFull?.ownerId);

      const rules =
        requiredFieldForSupplier || isAWGOwnerRequired
          ? [
              {
                required: true,
                message: `${label || 'This field'} is required`,
              },
            ]
          : null;

      const InputField = inputRender || inputCollection[field.type];

      const snapshotValue = dataQaSpecSnapshot?.[`${field?.name}`];

      const formItemProps = field?.formItemProps || {};

      return (
        <Col span={field.col || 24}>
          {field.isList ? (
            renderFormList(isEdit, field, sectionKey)
          ) : (
            <QaSpecProductSnapshot
              supplierModifiedValue={dataProductSpec?.[`${field?.name}`]}
              snapshotValue={snapshotValue}
              highlightContent={
                <HighlightContent
                  label={`${label}:`}
                  value={snapshotValue}
                  variant={field?.type}
                />
              }
            >
              <Form.Item
                key={field.name}
                name={field.name}
                label={
                  <TraceableCommentContainer
                    qaSpecName={COMMENT_FIELDS.productSpecification.qaSpecName}
                    qaSpecFieldName={label}
                  >
                    <QaSpecHelpContentLabel fieldPath={field.fieldPath}>
                      <Text ellipsis={{ tooltip: <strong>{label}</strong> }}>
                        <strong>{label}</strong>
                      </Text>
                    </QaSpecHelpContentLabel>
                  </TraceableCommentContainer>
                }
                rules={rules}
                {...formItemProps}
              >
                {typeof InputField === 'function'
                  ? InputField({ ...field, isEdit })
                  : InputField}
              </Form.Item>
            </QaSpecProductSnapshot>
          )}
        </Col>
      );
    });
  };

  const renderFormList = (isEdit, fieldSchema, sectionKey) => {
    const disabled = disabledCertifier[fieldSchema.name];

    const dataList = dataProductSpec?.[`${fieldSchema.name}`] ?? [];
    const dataListSnapshot = dataQaSpecSnapshot?.[`${fieldSchema.name}`] ?? [];
    const fieldPath = fieldSchema?.fieldPath ?? '';

    return (
      <Form.List
        name={fieldSchema.name}
        rules={[
          () => ({
            validator(_, value) {
              const isRequired = checkRequiredField(
                requiredFields,
                fieldSchema.name
              );

              if (value || !isRequired) {
                return Promise.resolve();
              }

              return Promise.reject(
                new Error(
                  `${intl.formatMessage(
                    Messages[sectionKey][fieldSchema.name]
                  )} is required`
                )
              );
            },
          }),
        ]}
      >
        {(fields, { add, remove }, { errors }) => {
          const handleAddField = () => {
            add();
            onAddField(fields?.length);
          };

          return (
            <>
              {fields.map(({ key, name, ...restField }, idx) => {
                const options = fieldSchema.enum
                  ? findProductPropertyEnums(fieldSchema.enum, productEnums)
                  : [];

                return (
                  <Row key={key} align='middle' style={{ flexWrap: 'nowrap' }}>
                    <Col flex={1}>
                      <QaSpecProductSnapshot
                        supplierModifiedValue={dataList[idx]}
                        snapshotValue={dataListSnapshot[idx]}
                        highlightContent={
                          <HighlightContent
                            label={null}
                            value={dataListSnapshot[idx]}
                            gutter={0}
                          />
                        }
                        key={`${key}-${idx}`}
                      >
                        <Form.Item
                          {...restField}
                          {...fieldSchema.layout}
                          name={name}
                          label={
                            <QaSpecHelpContentLabel fieldPath={fieldPath}>
                              <Text
                                ellipsis={{
                                  tooltip: (
                                    <>
                                      {intl.formatMessage(
                                        Messages[sectionKey][fieldSchema.name]
                                      )}
                                    </>
                                  ),
                                }}
                              >
                                {intl.formatMessage(
                                  Messages[sectionKey][fieldSchema.name]
                                )}
                              </Text>
                            </QaSpecHelpContentLabel>
                          }
                        >
                          {fieldSchema.type === 'select' &&
                          isEdit &&
                          !disabled ? (
                            <WrapperSelect style={{ width: 204 }}>
                              {options.map((option) =>
                                option ? (
                                  <Option
                                    key={option.enumDisplayName}
                                    value={option.enumDisplayName}
                                    disabled={selectedCertifier?.includes(
                                      option.enumDisplayName
                                    )}
                                  >
                                    {option.enumDisplayName}
                                  </Option>
                                ) : null
                              )}
                            </WrapperSelect>
                          ) : (
                            <PrimaryFieldSnapshot
                              ref={(ref) => inputRef(ref, idx)}
                              disabled={disabled}
                              readOnly={!isEdit}
                              variant='text'
                            />
                          )}
                        </Form.Item>
                      </QaSpecProductSnapshot>
                    </Col>
                    <Col flex={'30px'} style={{ paddingLeft: 16 }}>
                      <Form.Item>
                        <CloseOutlined
                          className='form-list-remove-btn'
                          onClick={() => remove(name)}
                          style={{ color: 'red' }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                );
              })}
              <Row className='form-list-add-btn' gutter={[5, 5]}>
                <Space>
                  <Button
                    block
                    onClick={() => handleAddField()}
                    icon={<PlusOutlined />}
                    disabled={disabled}
                    type='primary'
                  >
                    Add field
                  </Button>
                  <DietaryCertificateUploadBtn
                    fieldSchema={fieldSchema}
                    disabled={disabled}
                    openModalToUploadCertificate={openModalToUploadCertificate}
                  />
                </Space>
                <Form.ErrorList errors={errors} />
              </Row>
            </>
          );
        }}
      </Form.List>
    );
  };

  const renderLocation = (isEdit, section) => {
    return (
      <SectionWrapper
        className='qa-spec-product-spec__info-section'
        title={intl.formatMessage(Messages[section.key].title)}
      >
        <Row>{renderField(isEdit, section.fields, section.key)}</Row>
        <Row>
          <Col span={24}>
            <Form.List name='countryOfOrigin'>
              {(fields, { add, remove }) => (
                <>
                  <Form.Item
                    required={isRequiredCountryOfOrigin}
                    label={
                      <TraceableCommentContainer
                        qaSpecName={
                          COMMENT_FIELDS.productSpecification.qaSpecName
                        }
                        qaSpecFieldName={
                          COMMENT_FIELDS.productSpecification.locations
                            .countryOfOrigin
                        }
                      >
                        <QaSpecHelpContentLabel
                          fieldPath={
                            'qaSpecification.productSpecification.countryOfOrigin'
                          }
                        >
                          <Text>
                            {checkSectionRequired(
                              requiredFields,
                              'qaSpecCountryOfOrigin'
                            ) &&
                              isToggleRequiredFields && (
                                <>
                                  <Typography.Text strong type='danger'>
                                    *
                                  </Typography.Text>{' '}
                                </>
                              )}
                            <strong>Country of Origin</strong>
                          </Text>
                        </QaSpecHelpContentLabel>
                      </TraceableCommentContainer>
                    }
                  >
                    <Button
                      type='dashed'
                      block
                      onClick={() => add()}
                      icon={<PlusOutlined />}
                      disabled={!isEdit}
                    >
                      Add field
                    </Button>
                  </Form.Item>

                  {fields.map((field, index) => {
                    const { key, name, ...restField } = field;

                    const val = countryOfOrigin[name];

                    const snapshotCountryOfOrigin =
                      dataQaSpecSnapshot?.countryOfOrigin ?? [];
                    const snapshotCountryCode =
                      snapshotCountryOfOrigin?.[`${index}`]?.countryCode;
                    const snapshotCountrySubdivisionString =
                      snapshotCountryOfOrigin?.[
                        `${index}`
                      ]?.countrySubdivisionCode?.join(', ');

                    const countrySubdivisionString =
                      val?.countrySubdivisionCode?.join(', ');

                    return (
                      <Row
                        key={index}
                        align='middle'
                        style={{ flexWrap: 'nowrap' }}
                      >
                        <Col flex={'30px'} style={{ paddingRight: 8 }}>
                          <MinusCircleOutlined
                            className='form-list-remove-btn'
                            onClick={() => remove(name)}
                          />
                        </Col>
                        <Col flex={1}>
                          <SectionWrapper
                            className='qa-spec-product-spec__info-section'
                            title={`Country of Origin ${index + 1}`}
                          >
                            <QaSpecProductSnapshot
                              supplierModifiedValue={val?.countryCode}
                              snapshotValue={snapshotCountryCode}
                              highlightContent={
                                <HighlightContent
                                  label='Country Code:'
                                  value={snapshotCountryCode}
                                />
                              }
                            >
                              <Form.Item
                                {...restField}
                                name={[name, 'countryCode']}
                                label={
                                  <TraceableCommentContainer
                                    qaSpecName={
                                      COMMENT_FIELDS.productSpecification
                                        .qaSpecName
                                    }
                                    qaSpecFieldName='Country Code'
                                  >
                                    <QaSpecHelpContentLabel
                                      fieldPath={
                                        'qaSpecification.productSpecification.countryOfOrigin.countryCode'
                                      }
                                    >
                                      Country Code
                                    </QaSpecHelpContentLabel>
                                  </TraceableCommentContainer>
                                }
                                rules={[
                                  getRequiredRule(
                                    'countryOfOrigin.countryCode',
                                    'Country Code'
                                  ),
                                ]}
                              >
                                <PrimaryFieldSnapshot
                                  disabled={!isEdit}
                                  variant='select'
                                  showSearch
                                  filterOption={(input, option) =>
                                    option?.value
                                      ?.toLowerCase()
                                      .indexOf(input?.toLowerCase()) >= 0
                                  }
                                >
                                  {sortBy(
                                    countryCodeEnum?.[0]?.enumProperties,
                                    ['enumDisplayName', 'enumGroup']
                                  )?.map((item, index) => (
                                    <Option
                                      value={item?.enumDisplayName}
                                      key={index}
                                      readOnly={!isEdit}
                                    >
                                      {item?.enumDisplayName}
                                    </Option>
                                  ))}
                                </PrimaryFieldSnapshot>
                              </Form.Item>
                            </QaSpecProductSnapshot>

                            <QaSpecProductSnapshot
                              supplierModifiedValue={countrySubdivisionString}
                              snapshotValue={snapshotCountrySubdivisionString}
                              highlightContent={
                                <HighlightContent
                                  label='Country Subdivision Code:'
                                  value={snapshotCountrySubdivisionString}
                                />
                              }
                            >
                              <Form.Item
                                name={[name, 'countrySubdivisionCode']}
                                label={
                                  <TraceableCommentContainer
                                    qaSpecName={
                                      COMMENT_FIELDS.productSpecification
                                        .qaSpecName
                                    }
                                    qaSpecFieldName='Country Subdivision Code'
                                  >
                                    <QaSpecHelpContentLabel
                                      fieldPath={
                                        'qaSpecification.productSpecification.countryOfOrigin.countrySubdivisionCode'
                                      }
                                    >
                                      Country Subdivision Code
                                    </QaSpecHelpContentLabel>
                                  </TraceableCommentContainer>
                                }
                                rules={[
                                  getRequiredRule(
                                    'countryOfOrigin.countrySubdivisionCode',
                                    'Country Subdivision Code'
                                  ),
                                ]}
                              >
                                {isEdit ? (
                                  <WrapperSelect
                                    mode='multiple'
                                    showSearch
                                    filterOption={(input, option) =>
                                      option?.value
                                        ?.toLowerCase()
                                        .indexOf(input?.toLowerCase()) >= 0
                                    }
                                  >
                                    {sortBy(
                                      countrySubDivisionEnum?.[0]
                                        ?.enumProperties,
                                      ['enumDisplayName', 'enumGroup']
                                    )?.map((item, index) => (
                                      <Option
                                        value={item?.enumDisplayName}
                                        key={index}
                                        readOnly={!isEdit}
                                      >
                                        {item?.enumDisplayName}
                                      </Option>
                                    ))}
                                  </WrapperSelect>
                                ) : (
                                  <Tooltip title={countrySubdivisionString}>
                                    <PrimaryFieldSnapshot
                                      readOnly
                                      variant='text'
                                      value={countrySubdivisionString}
                                    />
                                  </Tooltip>
                                )}
                              </Form.Item>
                            </QaSpecProductSnapshot>
                          </SectionWrapper>
                        </Col>
                      </Row>
                    );
                  })}
                </>
              )}
            </Form.List>
          </Col>
        </Row>
      </SectionWrapper>
    );
  };

  const renderStatement = (isEdit, section) => {
    const isRequiredNonfoodStatement = checkSectionRequired(
      requiredFields,
      'nonfoodIngredientStatement'
    );

    return (
      <SectionWrapper
        className='qa-spec-product-spec__info-section'
        title={intl.formatMessage(Messages[section.key].title)}
      >
        <Row>{renderField(isEdit, section.fields, section.key)}</Row>
        <Row>
          <Col span={24}>
            <Form.List name='nonfoodIngredientStatement'>
              {(fields, { add, remove }) => (
                <>
                  <Form.Item
                    label={
                      <TraceableCommentContainer
                        qaSpecName={
                          COMMENT_FIELDS.productSpecification.qaSpecName
                        }
                        qaSpecFieldName={
                          COMMENT_FIELDS.productSpecification.statement
                            .nonfoodIngredientStatement
                        }
                      >
                        <QaSpecHelpContentLabel fieldPath='qaSpecification.productSpecification.nonfoodIngredientStatement'>
                          <Text>
                            {isRequiredNonfoodStatement &&
                              isToggleRequiredFields && (
                                <>
                                  <Typography.Text strong type='danger'>
                                    *
                                  </Typography.Text>{' '}
                                </>
                              )}
                            <strong>Nonfood Ingredient Statement</strong>
                          </Text>
                        </QaSpecHelpContentLabel>
                      </TraceableCommentContainer>
                    }
                  >
                    <Button
                      type='dashed'
                      block
                      onClick={() => add()}
                      icon={<PlusOutlined />}
                      disabled={!isEdit}
                    >
                      Add field
                    </Button>
                  </Form.Item>

                  {fields.map((field, index) => {
                    const { key, name, ...restField } = field;

                    const val = nonfoodIngredientStatement[name];

                    const nonfoodIngredientStatementSnapshot =
                      dataQaSpecSnapshot?.nonfoodIngredientStatement ?? [];

                    const nonfoodIngredientStatementItemSnapshot =
                      nonfoodIngredientStatementSnapshot?.[`${index}`] ?? {};

                    const {
                      value: valueSnapshot,
                      languageCode: languageCodeSnapshot,
                      codeListVersion: codeListVersionSnapshot,
                      formattingPattern: formattingPatternSnapshot,
                      sequenceNumber: sequenceNumberSnapshot,
                    } = nonfoodIngredientStatementItemSnapshot;

                    return (
                      <Row
                        key={key}
                        align='middle'
                        style={{ flexWrap: 'nowrap' }}
                      >
                        <Col flex={'30px'} style={{ paddingRight: 8 }}>
                          <MinusCircleOutlined
                            className='form-list-remove-btn'
                            onClick={() => remove(name)}
                          />
                        </Col>
                        <Col flex={1}>
                          <SectionWrapper
                            className='qa-spec-product-spec__info-section'
                            title={`Nonfood Ingredient Statement ${index + 1}`}
                          >
                            <QaSpecProductSnapshot
                              supplierModifiedValue={val?.value}
                              snapshotValue={valueSnapshot}
                              highlightContent={
                                <HighlightContent
                                  label='Value:'
                                  value={valueSnapshot}
                                />
                              }
                            >
                              <Form.Item
                                {...restField}
                                name={[name, 'value']}
                                label={
                                  <TraceableCommentContainer
                                    qaSpecName={
                                      COMMENT_FIELDS.productSpecification
                                        .qaSpecName
                                    }
                                    qaSpecFieldName='Value'
                                  >
                                    <QaSpecHelpContentLabel fieldPath='qaSpecification.productSpecification.nonfoodIngredientStatement.value'>
                                      Value
                                    </QaSpecHelpContentLabel>
                                  </TraceableCommentContainer>
                                }
                                rules={[
                                  getRequiredRule(
                                    'nonfoodIngredientStatement.value',
                                    'Value'
                                  ),
                                ]}
                              >
                                <PrimaryFieldSnapshot
                                  variant='text'
                                  readOnly={!isEdit}
                                />
                              </Form.Item>
                            </QaSpecProductSnapshot>
                            <QaSpecProductSnapshot
                              supplierModifiedValue={val?.languageCode}
                              snapshotValue={languageCodeSnapshot}
                              highlightContent={
                                <HighlightContent
                                  label='Language Code:'
                                  value={languageCodeSnapshot}
                                />
                              }
                            >
                              <Form.Item
                                {...restField}
                                name={[name, 'languageCode']}
                                label={
                                  <TraceableCommentContainer
                                    qaSpecName={
                                      COMMENT_FIELDS.productSpecification
                                        .qaSpecName
                                    }
                                    qaSpecFieldName='Language Code'
                                  >
                                    <QaSpecHelpContentLabel fieldPath='qaSpecification.productSpecification.nonfoodIngredientStatement.languageCode'>
                                      Language Code
                                    </QaSpecHelpContentLabel>
                                  </TraceableCommentContainer>
                                }
                                rules={[
                                  getRequiredRule(
                                    'nonfoodIngredientStatement.languageCode',
                                    'Language Code'
                                  ),
                                ]}
                              >
                                <PrimaryFieldSnapshot
                                  variant='select'
                                  disabled={!isEdit}
                                  showSearch
                                  filterOption={(input, option) =>
                                    option?.value
                                      ?.toLowerCase()
                                      .indexOf(input?.toLowerCase()) >= 0
                                  }
                                >
                                  {sortBy(
                                    languageCodeEnum?.[0]?.enumProperties,
                                    ['enumDisplayName', 'enumGroup']
                                  )?.map((item, index) => (
                                    <Option
                                      value={item?.enumCode}
                                      key={index}
                                      readOnly={!isEdit}
                                    >
                                      {`${
                                        item.enumDescription
                                      } - (${item.enumCode.toUpperCase()})`}
                                    </Option>
                                  ))}
                                </PrimaryFieldSnapshot>
                              </Form.Item>
                            </QaSpecProductSnapshot>

                            <QaSpecProductSnapshot
                              supplierModifiedValue={val?.codeListVersion}
                              snapshotValue={codeListVersionSnapshot}
                              highlightContent={
                                <HighlightContent
                                  label='Code List Version:'
                                  value={codeListVersionSnapshot}
                                />
                              }
                            >
                              <Form.Item
                                {...restField}
                                name={[name, 'codeListVersion']}
                                label={
                                  <TraceableCommentContainer
                                    qaSpecName={
                                      COMMENT_FIELDS.productSpecification
                                        .qaSpecName
                                    }
                                    qaSpecFieldName='Code List Version'
                                  >
                                    <QaSpecHelpContentLabel fieldPath='qaSpecification.productSpecification.nonfoodIngredientStatement.codeListVersion'>
                                      Code List Version
                                    </QaSpecHelpContentLabel>
                                  </TraceableCommentContainer>
                                }
                                rules={[
                                  getRequiredRule(
                                    'nonfoodIngredientStatement.codeListVersion',
                                    'Code List Version'
                                  ),
                                ]}
                              >
                                <PrimaryFieldSnapshot
                                  variant='text'
                                  readOnly={!isEdit}
                                />
                              </Form.Item>
                            </QaSpecProductSnapshot>

                            <QaSpecProductSnapshot
                              supplierModifiedValue={val?.formattingPattern}
                              snapshotValue={formattingPatternSnapshot}
                              highlightContent={
                                <HighlightContent
                                  label='Formatting Pattern:'
                                  value={formattingPatternSnapshot}
                                />
                              }
                            >
                              <Form.Item
                                {...restField}
                                name={[name, 'formattingPattern']}
                                label={
                                  <TraceableCommentContainer
                                    qaSpecName={
                                      COMMENT_FIELDS.productSpecification
                                        .qaSpecName
                                    }
                                    qaSpecFieldName='Formatting Pattern'
                                  >
                                    <QaSpecHelpContentLabel fieldPath='qaSpecification.productSpecification.nonfoodIngredientStatement.formattingPattern'>
                                      Formatting Pattern
                                    </QaSpecHelpContentLabel>
                                  </TraceableCommentContainer>
                                }
                                rules={[
                                  getRequiredRule(
                                    'nonfoodIngredientStatement.formattingPattern',
                                    'Formatting Pattern'
                                  ),
                                ]}
                              >
                                <PrimaryFieldSnapshot
                                  variant='text'
                                  readOnly={!isEdit}
                                />
                              </Form.Item>
                            </QaSpecProductSnapshot>
                            <QaSpecProductSnapshot
                              supplierModifiedValue={val?.sequenceNumber}
                              snapshotValue={sequenceNumberSnapshot}
                              highlightContent={
                                <HighlightContent
                                  label='Sequence Number:'
                                  value={sequenceNumberSnapshot}
                                />
                              }
                            >
                              <Form.Item
                                {...restField}
                                name={[name, 'sequenceNumber']}
                                label={
                                  <TraceableCommentContainer
                                    qaSpecName={
                                      COMMENT_FIELDS.productSpecification
                                        .qaSpecName
                                    }
                                    qaSpecFieldName='Sequence Number'
                                  >
                                    <QaSpecHelpContentLabel fieldPath='qaSpecification.productSpecification.nonfoodIngredientStatement.sequenceNumber'>
                                      Sequence Number
                                    </QaSpecHelpContentLabel>
                                  </TraceableCommentContainer>
                                }
                                rules={[
                                  getRequiredRule(
                                    'nonfoodIngredientStatement.sequenceNumber',
                                    'Sequence Number'
                                  ),
                                ]}
                              >
                                <PrimaryFieldSnapshot
                                  variant='number'
                                  readOnly={!isEdit}
                                />
                              </Form.Item>
                            </QaSpecProductSnapshot>
                          </SectionWrapper>
                        </Col>
                      </Row>
                    );
                  })}
                </>
              )}
            </Form.List>
          </Col>
        </Row>
      </SectionWrapper>
    );
  };

  const renderMeatFishField = (isEdit) => {
    const { usdaGrade, usdaShieldId, eggsGrading } = dataProductSpec ?? {};

    const {
      usdaGrade: snapshotUsdaGrade,
      usdaShieldId: snapshotUsdaShieldId,
      eggsGrading: snapshotEggsGrading,
    } = dataQaSpecSnapshot ?? {};

    return (
      <Row
        id='meat-fish-field'
        gutter={[16, 0]}
        // style={{ marginTop: 24, pointerEvents: isEdit ? 'auto' : 'none' }}
        style={{ marginTop: 24 }}
      >
        <Col span={24}>
          <QaSpecProductSnapshot
            supplierModifiedValue={usdaGrade}
            snapshotValue={snapshotUsdaGrade}
            highlightContent={
              <HighlightContent
                label='USDA / Canadian Grading:'
                value={snapshotUsdaGrade}
              />
            }
          >
            <Form.Item
              name='usdaGrade'
              label={
                <TraceableCommentContainer
                  qaSpecName={COMMENT_FIELDS.productSpecification.qaSpecName}
                  qaSpecFieldName={
                    COMMENT_FIELDS.productSpecification.uSDACanadianGrading
                  }
                >
                  <QaSpecHelpContentLabel fieldPath='qaSpecification.productSpecification.usdaGrade'>
                    <Typography.Text strong>
                      USDA / Canadian Grading
                    </Typography.Text>
                  </QaSpecHelpContentLabel>
                </TraceableCommentContainer>
              }
              rules={[getRequiredRule('usdaGrade', 'USDA / Canadian Grading')]}
            >
              <PrimaryFieldSnapshot
                variant='select'
                allowClear
                showSearch
                getPopupContainer={() =>
                  document.getElementById('meat-fish-field')
                }
                disabled={!isEdit}
              >
                {sortByKey(usdaGradingList, 'enumDisplayName')?.map(
                  (property, index) => (
                    <Select.Option
                      value={property.enumDisplayName}
                      key={property.enumDisplayName + index}
                    >
                      {property.enumDisplayName
                        ? property.enumDisplayName
                        : property.enumDescription}
                    </Select.Option>
                  )
                )}
              </PrimaryFieldSnapshot>
            </Form.Item>
          </QaSpecProductSnapshot>
        </Col>
        <Col span={24}>
          <QaSpecProductSnapshot
            supplierModifiedValue={usdaShieldId}
            snapshotValue={snapshotUsdaShieldId}
            highlightContent={
              <HighlightContent
                label='USDA Shield ID'
                value={snapshotUsdaShieldId}
              />
            }
          >
            <Form.Item
              name='usdaShieldId'
              label={
                <TraceableCommentContainer
                  qaSpecName={COMMENT_FIELDS.productSpecification.qaSpecName}
                  qaSpecFieldName={
                    COMMENT_FIELDS.productSpecification.uSDAShieldID
                  }
                >
                  <QaSpecHelpContentLabel fieldPath='qaSpecification.productSpecification.usdaShieldId'>
                    <Typography.Text strong>USDA Shield ID</Typography.Text>
                  </QaSpecHelpContentLabel>
                </TraceableCommentContainer>
              }
              rules={[getRequiredRule('usdaGrade', 'USDA Shield ID')]}
            >
              <PrimaryFieldSnapshot variant='text' />
            </Form.Item>
          </QaSpecProductSnapshot>
        </Col>
        <Col span={24}>
          <Form.Item
            label={
              <TraceableCommentContainer
                qaSpecName={COMMENT_FIELDS.productSpecification.qaSpecName}
                qaSpecFieldName={
                  COMMENT_FIELDS.productSpecification
                    .certificateBadgeOrOtherCertifications
                }
              >
                <QaSpecHelpContentLabel
                  fieldPath={
                    TOOlTIP_FIELDS.productSpecificationCertificateBadge
                      .fieldPath
                  }
                >
                  <Typography.Text
                    strong
                    ellipsis={{
                      tooltip: 'Certificate Badge or Other Certifications',
                    }}
                  >
                    Certificate Badge or Other Certifications
                  </Typography.Text>
                </QaSpecHelpContentLabel>
              </TraceableCommentContainer>
            }
          >
            <Button
              icon={<UploadOutlined />}
              danger
              disabled={!isEdit}
              onClick={() => setVisible(true)}
            >
              Upload certificate document
            </Button>
          </Form.Item>
        </Col>
        <Col span={24}>
          <QaSpecProductSnapshot
            supplierModifiedValue={eggsGrading}
            snapshotValue={snapshotEggsGrading}
            highlightContent={
              <HighlightContent
                label='Eggs Grading:'
                value={snapshotEggsGrading}
              />
            }
          >
            <Form.Item
              name='eggsGrading'
              label={
                <TraceableCommentContainer
                  qaSpecName={COMMENT_FIELDS.productSpecification.qaSpecName}
                  qaSpecFieldName={
                    COMMENT_FIELDS.productSpecification.eggsGrading
                  }
                >
                  <QaSpecHelpContentLabel fieldPath='qaSpecification.productSpecification.eggsGrading'>
                    <Typography.Text strong>Eggs Grading</Typography.Text>
                  </QaSpecHelpContentLabel>
                </TraceableCommentContainer>
              }
              rules={[getRequiredRule('eggsGrading', 'Eggs Grading')]}
            >
              <PrimaryFieldSnapshot
                variant='select'
                allowClear
                getPopupContainer={() =>
                  document.getElementById('meat-fish-field')
                }
                disabled={!isEdit}
              >
                {sortByKey(eggsGradingList, 'enumDisplayName')?.map(
                  (property, index) => (
                    <Select.Option
                      value={property.enumDisplayName}
                      key={property.enumDisplayName + index}
                    >
                      {property.enumDisplayName
                        ? property.enumDisplayName
                        : property.enumDescription}
                    </Select.Option>
                  )
                )}
              </PrimaryFieldSnapshot>
            </Form.Item>
          </QaSpecProductSnapshot>
        </Col>
        {visible && (
          <UploadCertificate
            visible={visible}
            setVisible={setVisible}
            productId={productId}
            defaultValue={{ certificateType: 'Other' }}
            qaSpecName='Product Specification'
          />
        )}
      </Row>
    );
  };

  // const TextAreaField = ({ readOnly, item }) => {
  //   const { displayName, fieldName, limitCharacters } = item;
  //   return (
  //     <Form.Item
  //       name={fieldName}
  //       label={<Typography.Text strong>{displayName}</Typography.Text>}
  //       labelCol={{ span: 24 }}
  //       wrapperCol={{ span: 24 }}
  //     >
  //       <Input.TextArea
  //         maxLength={limitCharacters}
  //         showCount
  //         readOnly={readOnly}
  //       />
  //     </Form.Item>
  //   );
  // };

  const TextAreaField = ({ readOnly, item }) => {
    const { displayName, fieldName, limitCharacters } = item;
    const supplierModifiedValue = dataProductSpec?.[`${fieldName}`];
    const snapshotValue = dataQaSpecSnapshot?.[`${fieldName}`];
    const isRequired = checkRequiredField(requiredFields, fieldName);

    return (
      <QaSpecProductSnapshot
        supplierModifiedValue={supplierModifiedValue}
        snapshotValue={snapshotValue}
        highlightContent={
          <HighlightContent label={`${displayName}:`} value={snapshotValue} />
        }
      >
        <Form.Item
          name={fieldName}
          label={
            <TraceableCommentContainer
              qaSpecName={COMMENT_FIELDS.productSpecification.qaSpecName}
              qaSpecFieldName={displayName}
            >
              <QaSpecHelpContentLabel fieldPath={item.fieldPath}>
                <Typography.Text strong>{displayName}</Typography.Text>
              </QaSpecHelpContentLabel>
            </TraceableCommentContainer>
          }
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          rules={[
            {
              required: isRequired,
              message: `${displayName || 'This field'} is required`,
            },
          ]}
        >
          <PrimaryFieldSnapshot
            variant='textarea'
            maxLength={limitCharacters}
            showCount
            readOnly={readOnly}
          />
        </Form.Item>
      </QaSpecProductSnapshot>
    );
  };

  return (
    <WithLoading loading={loading}>
      <div className='qa-spec-product-spec__info'>
        <EditContainer
          type={EDIT_SECTION.PROD_SPEC_INFO}
          productId={productId}
          handleSave={onSubmit}
          handleCancel={onCancel}
          float
        >
          {(isEdit) => {
            return (
              <Form
                className={classnames('qa-spec-product-spec__info-form', {
                  'qa-spec-product-spec__info-form--edit': isEdit,
                })}
                form={form}
                {...layoutCol}
                autoComplete='off'
                onValuesChange={onValuesChange}
              >
                <EditTabSectionWrapper.CheckerWrapper
                  isEdit={isEdit}
                  type={EDIT_SECTION.PROD_SPEC_INFO}
                  onSubmit={onSubmit}
                >
                  <Row
                    gutter={[16, 12]}
                    // style={{ pointerEvents: isEdit ? 'auto' : 'none' }}
                  >
                    <Col span={12} xxl={8}>
                      {renderSection(
                        isEdit,
                        PRODUCT_SPEC_FORM_SECTION_SCHEMA.DATE_CODING
                      )}
                      {renderStatement(
                        isEdit,
                        PRODUCT_SPEC_FORM_SECTION_SCHEMA.STATEMENT
                      )}
                      {renderLocation(
                        isEdit,
                        PRODUCT_SPEC_FORM_SECTION_SCHEMA.LOCATION
                      )}
                      <div style={{ padding: '23px 10px 10px 10px' }}>
                        <QaSpecProductSnapshot
                          supplierModifiedValue={dataProductSpec?.wicCompliant}
                          snapshotValue={dataQaSpecSnapshot?.wicCompliant}
                          highlightContent={
                            <HighlightContent
                              label={intl.formatMessage(
                                Messages.USDA_WIC_eligible_label
                              )}
                              value={dataQaSpecSnapshot?.wicCompliant}
                              variant='checkbox'
                            />
                          }
                        >
                          <Form.Item
                            name={'wicCompliant'}
                            label={
                              <TraceableCommentContainer
                                qaSpecName={
                                  COMMENT_FIELDS.productSpecification.qaSpecName
                                }
                                qaSpecFieldName={
                                  COMMENT_FIELDS.productSpecification
                                    .uSDAWICEligible
                                }
                              >
                                <QaSpecHelpContentLabel fieldPath='qaSpecification.productSpecification.usdaWicEligible'>
                                  <Text>
                                    <strong>
                                      {intl.formatMessage(
                                        Messages.USDA_WIC_eligible_label
                                      )}
                                    </strong>
                                  </Text>
                                </QaSpecHelpContentLabel>
                              </TraceableCommentContainer>
                            }
                            rules={[
                              getRequiredRule(
                                'wicCompliant',
                                intl.formatMessage(
                                  Messages.USDA_WIC_eligible_label
                                )
                              ),
                            ]}
                          >
                            <PrimaryFieldSnapshot
                              variant='checkbox'
                              disabled={!isEdit}
                            />
                          </Form.Item>
                        </QaSpecProductSnapshot>
                        <div style={{ position: 'relative' }}>
                          <QaSpecProductSnapshot
                            supplierModifiedValue={dataProductSpec?.msds}
                            snapshotValue={dataQaSpecSnapshot?.msds}
                            highlightContent={
                              <HighlightContent
                                label={intl.formatMessage(
                                  Messages.MSDSRequired
                                )}
                                value={dataQaSpecSnapshot?.msds}
                                variant='checkbox'
                              />
                            }
                          >
                            <Form.Item
                              name={'msds'}
                              label={
                                <TraceableCommentContainer
                                  qaSpecName={
                                    COMMENT_FIELDS.productSpecification
                                      .qaSpecName
                                  }
                                  qaSpecFieldName={
                                    COMMENT_FIELDS.productSpecification
                                      .mSDSRequired
                                  }
                                >
                                  <QaSpecHelpContentLabel fieldPath='qaSpecification.productSpecification.msds'>
                                    <Text>
                                      <strong>
                                        {intl.formatMessage(
                                          Messages.MSDSRequired
                                        )}
                                      </strong>
                                    </Text>
                                  </QaSpecHelpContentLabel>
                                </TraceableCommentContainer>
                              }
                              rules={[
                                getRequiredRule(
                                  'msds',
                                  intl.formatMessage(Messages.MSDSRequired)
                                ),
                              ]}
                            >
                              <MsdsRequiredField
                                isEdit={isEdit}
                                msdsWatch={msdsWatch}
                                handleOpenModalMSDSDocument={
                                  handleOpenModalMSDSDocument
                                }
                              />
                            </Form.Item>
                          </QaSpecProductSnapshot>
                        </div>
                      </div>
                    </Col>

                    <Col span={12} xxl={8}>                     
                      {renderSection(
                        isEdit,
                        PRODUCT_SPEC_FORM_SECTION_SCHEMA.SHELF_LIFE
                      )}
                      {renderSection(
                        isEdit,
                        PRODUCT_SPEC_FORM_SECTION_SCHEMA.DIETARY_WITHOUT_CERTIFICATION
                      )}
                      {renderSection(
                        isEdit,
                        PRODUCT_SPEC_FORM_SECTION_SCHEMA.CASE_TEMPERATURE
                      )}
                      
                    </Col>
                    <Col span={12} xxl={8}>
                      {DAIRY_SECTION_TEXT_AREA.map((item, index) => {
                        return (
                          <TextAreaField
                            key={index}
                            readOnly={!isEdit}
                            item={item}
                          />
                        );
                      })}
                      {/* Moved to claims list <QaSpecProductSnapshot
                        supplierModifiedValue={dataProductSpec?.glutenFreeClaim}
                        snapshotValue={dataQaSpecSnapshot?.glutenFreeClaim}
                        highlightContent={
                          <HighlightContent
                            label='Gluten Free Claim:'
                            value={dataQaSpecSnapshot?.glutenFreeClaim}
                            variant='checkbox'
                          />
                        }
                      >
                        <Form.Item
                          name='glutenFreeClaim'
                          label={
                            <TraceableCommentContainer
                              qaSpecName={
                                COMMENT_FIELDS.productSpecification.qaSpecName
                              }
                              qaSpecFieldName={
                                COMMENT_FIELDS.productSpecification
                                  .glutenFreeClaim
                              }
                            >
                              <QaSpecHelpContentLabel
                                style={{ marginRight: 4 }}
                                fieldPath='qaSpecification.productSpecification.glutenFreeClaim'
                              >
                                <Typography.Text strong>
                                  Gluten Free Claim
                                </Typography.Text>
                              </QaSpecHelpContentLabel>
                            </TraceableCommentContainer>
                          }
                          labelCol={{ flex: '128x' }}
                          rules={[
                            {
                              required: checkRequiredField(
                                requiredFields,
                                'glutenFreeClaim'
                              ),
                              message: 'Gluten Free Claim is required',
                            },
                          ]}
                        >
                          <PrimaryFieldSnapshot
                            variant='checkbox'
                            disabled={!isEdit}
                          />
                        </Form.Item>
                      </QaSpecProductSnapshot> */}
                      {DAIRY_SECTION_TEXT_AREA_2.map((item, index) => {
                        return (
                          <TextAreaField
                            key={index}
                            readOnly={!isEdit}
                            item={item}
                          />
                        );
                      })}
                      {renderMeatFishField(isEdit)}
                    </Col>
                  </Row>
                </EditTabSectionWrapper.CheckerWrapper>
              </Form>
            );
          }}
        </EditContainer>
      </div>
      {isLinkToNewAssetModalOpen && (
        <UploadCertificate
          productId={productId}
          visible={isLinkToNewAssetModalOpen}
          setVisible={setIsLinkToNewAssetModalOpen}
          defaultValue={{ certificateType }}
          disableCertificateType
          qaSpecName='Product Specification'
        />
      )}
      {isAddDocumentForMSDS && (
        <CreateMSDSDocumentModal
          onCloseModal={handCloseModalUploadDocMSDS}
          certificateType={certificateType}
          getDocument={() => {
            handCloseModalUploadDocMSDS();
          }}
        />
      )}
    </WithLoading>
  );
};

const MsdsRequiredField = (props) => {
  const { isEdit, msdsWatch, handleOpenModalMSDSDocument, ...restProps } =
    props;

  const lowercaseMsdsWatch = msdsWatch?.toLowerCase();

  return (
    <div style={{ display: 'flex' }}>
      <PrimaryFieldSnapshot
        variant='checkbox'
        disabled={!isEdit}
        {...restProps}
      />
      {lowercaseMsdsWatch === 'yes' && (
        <Button
          style={{ marginLeft: 20 }}
          type='primary'
          onClick={handleOpenModalMSDSDocument}
          disabled={!isEdit || lowercaseMsdsWatch !== 'yes'}
        >
          Load
        </Button>
      )}
    </div>
  );
};

const QaSpecProductSnapshot = (props) => {
  const isToggleSnapshot = useCheckSnapshotForRetailer();

  const { isSuccess, dataQaSpecSnapshot } = useGetQaSpecSnapshot();

  if (isSuccess && isToggleSnapshot && dataQaSpecSnapshot) {
    return <RetailerSnapshot {...props} />;
  }

  return <>{props.children}</>;
};

export default QaSpecProductInfo;
